/**
 * SIP password decryption (shared crypto)
 *
 * Decrypts backend-encrypted sip_password: AES-GCM with 12-byte nonce,
 * payload = base64url(nonce || ciphertextWithAuthTag), key = SHA-256(secret).
 */

// ============================================================================
// Base64url decode
// ============================================================================

function base64UrlDecodeToBytes(base64url: string): Uint8Array {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ============================================================================
// Key derivation (SHA-256)
// ============================================================================

async function sha256(secret: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(secret);
  return crypto.subtle.digest('SHA-256', data);
}

// ============================================================================
// Decrypt (AES-GCM)
// ============================================================================

/**
 * Decrypts a backend-encrypted sip_password.
 * Backend format: base64url(nonce(12) || ciphertextWithAuthTag), key = SHA-256(secret).
 *
 * @param encryptedBase64url - Value from API (sip_password)
 * @param secretKey - Same secret as backend SYMMETRIC_ENCRYPTION_KEY
 * @returns Decrypted plaintext password, or null on error
 */
export async function decryptSipPassword(encryptedBase64url: string, secretKey: string): Promise<string | null> {
  if (!encryptedBase64url?.trim() || !secretKey?.trim()) {
    return null;
  }

  try {
    const payload = base64UrlDecodeToBytes(encryptedBase64url.trim());
    if (payload.length < 12 + 16) {
      // 12 nonce + at least 16 (auth tag)
      return null;
    }

    const nonce = payload.slice(0, 12);
    const ciphertext = payload.slice(12);

    const keyBytes = await sha256(secretKey);
    const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['decrypt']);

    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key, ciphertext);

    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}
